import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArticleIcon from "@mui/icons-material/Article";
import BarChartIcon from "@mui/icons-material/BarChart";
import CodeIcon from "@mui/icons-material/Code";
import DataObjectIcon from "@mui/icons-material/DataObject";
import DeleteIcon from "@mui/icons-material/Delete";
import ExtensionIcon from "@mui/icons-material/Extension";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import RunIcon from "@mui/icons-material/PlayArrow";
import SendIcon from "@mui/icons-material/Send";
import StreamIcon from "@mui/icons-material/Stream";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import WidgetsIcon from "@mui/icons-material/Widgets";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popover,
  Switch,
  SxProps,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { CasLoader } from "./brand/CasLoader";
import React, { ReactNode } from "react";
import { useMenu } from "../hook/menu";
import {
  useNotebookPage,
  useNotebookPageAccess,
  useNotebookPageIsPublished,
  usePageBackPath,
  usePageNameEditor,
  useSelectedNotebookPageIdMap,
  useSetNoteName,
} from "../hook/page";
import { NotebookPage } from "../types/note";
import { DynamicTextField } from "./dynamicTextField";
import { GoBackButton } from "./goBack";
import { EditPageMenu } from "./menu/page";
import { NavigatePageImplementationsButton } from "./page/implementation";
import { RefreshPageButton } from "./page/button/refresh";

export const NoteBlockContainerLegacy: React.FC<{
  children: ReactNode;
  name: string;
  description?: string;
  onDelete?: () => any;
  onRun?: () => any;
  isLoading?: boolean;
  isPaused?: boolean;
  onTogglePaused?: () => any;
}> = ({
  children,
  name,
  onDelete,
  description,
  onRun,
  isLoading = false,
  isPaused = true,
  onTogglePaused,
}) => {
  return (
    <Paper
      elevation={3}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        padding: "2rem",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h5">{name}</Typography>
          <Typography variant="subtitle2">{description}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {isLoading && (
            <CasLoader
              size={28}
              sx={{
                ml: 1,
                color: "primary.main",
                position: "relative",
                display: "block",
              }}
            />
          )}
          {onTogglePaused && (
            <>
              <Tooltip
                title={
                  isPaused ? "Resume Scheduled Runs" : "Pause Scheduled Runs"
                }
              >
                <Switch
                  checked={!isPaused}
                  onChange={onTogglePaused}
                  disabled={isLoading}
                  color="primary"
                  aria-label="Toggle Is Running On Schedule"
                />
              </Tooltip>
            </>
          )}
          {onRun && (
            <>
              <IconButton color="primary" onClick={onRun} disabled={isLoading}>
                <RunIcon />
              </IconButton>
            </>
          )}
          {onDelete && (
            <IconButton color="primary" onClick={onDelete} disabled={isLoading}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
};

const BLOCK_ICONS: Record<string, React.ElementType> = {
  Page: ArticleIcon,
  Component: ExtensionIcon,
  Protocol: AccountTreeIcon,
  Implementation: CodeIcon,
  Credential: SendIcon,
  "Log Stream": StreamIcon,
  Chart: BarChartIcon,
  Code: DataObjectIcon,
  Package: Inventory2Icon,
  Subscription: SyncAltIcon,
};

export const NoteBlockContainer: React.FC<{
  children?: ReactNode;
  name?: string;
  description?: string;
  toolbar?: ReactNode;
}> = ({ children, toolbar, name = "", description }) => {
  const IconComponent = BLOCK_ICONS[name] ?? WidgetsIcon;

  return (
    <Box
      sx={{
        borderRadius: 1,
        "&:hover": { bgcolor: "action.hover" },
        padding: "0.5rem",
        width: "100%",
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 1,
      }}
    >
      <Tooltip title={name} placement="left">
        <IconComponent
          sx={{ fontSize: 18, color: "text.disabled", flexShrink: 0, mt: "2px" }}
        />
      </Tooltip>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          gap: 1,
        }}
      >
        {description && (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
            {description}
          </Typography>
        )}
        {toolbar && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {toolbar}
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
};

export const NotePageContainer: React.FC<{
  pageName?: string;
  onEditPageName?: (name: string) => void;
  children?: ReactNode;
  sx?: SxProps;
}> = ({ pageName, children, sx, onEditPageName }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        ...sx,
      }}
    >
      {onEditPageName && pageName ? (
        <DynamicTextField
          textSx={{
            ...theme.typography.h4,
          }}
          text={pageName}
          setText={onEditPageName}
        />
      ) : pageName ? (
        <Typography variant="h4">{pageName}</Typography>
      ) : (
        <></>
      )}
      {children}
    </Box>
  );
};

export const ShareNoteButton: React.FC<{ page?: NotebookPage }> = ({
  page,
}) => {
  const { notebookPageIsPublished, setNotebookPageIsPublishedBackend } =
    useNotebookPageIsPublished(page?.["page-id"]);
  const { anchorEl, handleOpen, onClose, open, setAnchorEl, anchorToClick } =
    useMenu();
  const { writeAccess } = useNotebookPageAccess(page?.["page-id"]);

  return (
    <>
      <Popover anchorEl={anchorEl} open={open} onClose={onClose}>
        <Box sx={{ margin: 1, minWidth: "20rem" }}>
          <Typography variant="h6">Publish To The Web</Typography>
          <List>
            <ListItem>
              <ListItemText>Published</ListItemText>
              <Switch
                onChange={(e) => {
                  setNotebookPageIsPublishedBackend(!notebookPageIsPublished);
                }}
                checked={notebookPageIsPublished}
              />
            </ListItem>
          </List>
          <Box></Box>
        </Box>
      </Popover>
      {writeAccess && (
        <Button
          onClick={handleOpen}
          // ref={(e: any) => setAnchorEl(e)}
          variant="outlined"
        >
          Share
        </Button>
      )}
    </>
  );
};

export const NotePageHeader: React.FC<{ pageId: string | undefined }> = ({
  pageId,
}) => {
  const { page } = useNotebookPage(pageId);
  const folderId = page?.["folder-id"];
  const theme = useTheme();
  const { handleOpen, onClose, open, anchorEl } = useMenu();
  const pageBackPath = usePageBackPath();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const { writeAccess } = useNotebookPageAccess(selectedNotebookPageId);
  const { pageName, setPageName } = usePageNameEditor(pageId);

  if (!page) return <></>;

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <GoBackButton href={pageBackPath(pageId)} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <RefreshPageButton pageId={selectedNotebookPageId} />
          <ShareNoteButton page={page} />
          <NavigatePageImplementationsButton />
        </Box>
      </Box>
      <EditPageMenu
        anchorEl={anchorEl}
        onClose={onClose}
        open={open}
        pageId={pageId!}
      />
      <Box onContextMenu={handleOpen}>
        <DynamicTextField
          disabled={!writeAccess}
          textSx={{
            ...theme.typography.h4,
          }}
          text={pageName!}
          setText={setPageName}
        />
      </Box>
    </>
  );
};
